import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useApp } from '../context/AppContext'
import { useAuth } from '../context/AuthContext'
import { getDaysUntil, getInitials } from '../data/mockData'

export default function Profile() {
  const navigate = useNavigate()
  const { tasks, events, invitations, acceptInvitation, declineInvitation } = useApp()
  const { user, logout } = useAuth()
  const myTasks = tasks.filter(t => t.assignedToId === 'user-1' && !t.done)
  const doneTasks = tasks.filter(t => t.assignedToId === 'user-1' && t.done)
  const [notifs, setNotifs] = useState({ deadlines: true, comments: true, activity: false })

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (err) {
      console.error('Logout fejl:', err)
    }
  }

  return (
    <AppLayout>
      <div className="page-content" style={{ paddingTop: 20 }}>

        {/* Hero */}
        <div className="profile-hero">
          <div className="profile-av-big">
            {getInitials(user?.displayName || 'Bruger')}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="profile-name">{user?.displayName || 'Bruger'}</div>
            <div className="profile-email">{user?.email}</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div className="profile-event-count">{events.length}</div>
            <div className="profile-event-label">events</div>
          </div>
        </div>

        {/* My events */}
        <div>
          <p className="section-title">Mine events</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {events.map(event => {
              const myRole = event.members?.find(m => m.id === 'user-1')?.role
              const days = getDaysUntil(event.date)
              return (
                <Link key={event.id} to={`/event/${event.id}`} className="event-card-link">
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: event.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.name}</p>
                    <p style={{ fontSize: 10, color: '#DF83A9', marginTop: 2 }}>
                      {myRole === 'owner' ? 'arrangør' : 'medhjælper'} · {days} dage
                    </p>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Invitations */}
        {invitations.length > 0 && (
          <div>
            <p className="section-title">Ventende invitationer</p>
            {invitations.map(inv => (
              <div key={inv.id} style={{ background: '#f0eeff', borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: inv.eventColor, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>{inv.eventName}</p>
                  <p style={{ fontSize: 10, color: '#DF83A9', marginTop: 2 }}>fra {inv.fromName}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" onClick={async e => { e.stopPropagation(); try { await acceptInvitation(inv) } catch (err) { console.error('Accept error:', err) } }} style={{ background: '#9B8B7A', color: '#fff', border: 'none', borderRadius: 9, padding: '5px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>ja tak</button>
                  <button type="button" onClick={async e => { e.stopPropagation(); try { await declineInvitation(inv.id) } catch (err) { console.error('Decline error:', err) } }} style={{ background: '#fff', color: '#DF83A9', border: 'none', borderRadius: 9, padding: '5px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>nej</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My tasks */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p className="section-title" style={{ marginBottom: 0 }}>Mine opgaver</p>
            <span className="pill pill-purple">{doneTasks.length} Klaret</span>
          </div>
          <div className="card">
            {myTasks.map(task => (
              <div key={task.id} style={{ padding: '10px 0', borderBottom: '1.5px solid #f0eeff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: task.eventColor || '#6c63ff', flexShrink: 0 }} />
                  <p style={{ fontSize: 12, fontWeight: 500, color: '#1a1a2e', flex: 1 }}>{task.title}</p>
                  <span className={`pill pill-${task.priority}`}>{task.priority === 'high' ? 'høj' : task.priority === 'medium' ? 'middel' : 'lav'}</span>
                </div>
                <p style={{ fontSize: 10, color: '#DF83A9', marginTop: 4, paddingLeft: 14 }}>{task.eventName}{task.deadline ? ` · ${task.deadline}` : ''}</p>
              </div>
            ))}
            {myTasks.length === 0 && <p style={{ fontSize: 12, color: '#DF83A9', textAlign: 'center', padding: '16px 0' }}>Ingen aktive opgaver</p>}
          </div>
        </div>

        {/* Notifications */}
        <div>
          <p className="section-title">Notifikationer</p>
          <div className="card">
            {[
              { key: 'deadlines', label: 'deadline påmindelser', sub: 'få besked 3 dage før' },
              { key: 'comments', label: 'nye kommentarer', sub: 'når nogen kommenterer dine opgaver' },
              { key: 'activity', label: 'aktivitet fra teamet', sub: 'når opgaver markeres som færdig' },
            ].map(n => (
              <div key={n.key} className="notif-row">
                <div>
                  <p className="notif-label">{n.label}</p>
                  <p className="notif-sub">{n.sub}</p>
                </div>
                <div
                  className={`toggle-switch ${notifs[n.key] ? 'on' : ''}`}
                  onClick={() => setNotifs(prev => ({ ...prev, [n.key]: !prev[n.key] }))}
                >
                  <div className="toggle-knob" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <button className="btn-danger" onClick={handleLogout}>log ud</button>

      </div>
    </AppLayout>
  )
}
